import { Button, ButtonGroup, Grid, GridItem, VStack, useToast } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { type Resolver, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";

import { COUNTRY_OPTIONS } from "@equidefi/constants/countries";
import { INTERNATIONAL, US_CITIZEN } from "@equidefi/constants/profile";
import { useDisclosure } from "@equidefi/hooks";
import { dayjs } from "@equidefi/i18n";
import { extractErrorMessage } from "@equidefi/portals";
import { SmartFormProvider } from "@equidefi/portals/components/smart-form";
import { useInvestmentNoteCreate } from "@equidefi/portals/hooks/useNotes";
import { usePostalCodeExcluded } from "@equidefi/portals/hooks/usePostalCode";
import { useCreateSigner } from "@equidefi/portals/hooks/useSigners";
import { useGetVault, useUpdateVault } from "@equidefi/portals/hooks/useVault";
import type { DTO } from "@equidefi/shared/types";
import { BannerAlert, Heading, Paper, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import { ENTITY } from "@equidefi/shared";
import { useInvestmentContext } from "./InvestmentContext";
import { type IndividualSchema, buildSchema } from "./editIndividualSchema";
import { ConfirmChangesModal } from "./modals/ConfirmChangesModal";

const yesNoOptions = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

export default function EditIndividualForm(): React.JSX.Element {
  const { vaultId } = useParams();
  const navigate = useNavigate();
  const { data: vault, isLoading: isVaultLoading } = useGetVault(vaultId);

  // ":vaultId/edit" is a single route node, so ".." resolves to the parent "investors" route.
  const backTo = "..";

  if (vaultId && isVaultLoading) {
    return (
      <VStack justify="flex-start" align="start" gap={2} flex={2} w="full">
        <Button variant="link" colorScheme="blue" onClick={() => navigate(backTo)} gap="2">
          <Icon.ArrowLeft /> Back to Investors Tab
        </Button>
        <Paper p="5" w="full">
          <Heading fontWeight="bold">{vaultId ? "Edit Individual" : "Add Individual"}</Heading>
          <Text mt={4}>Loading…</Text>
        </Paper>
      </VStack>
    );
  }

  if (vaultId && !vault) {
    return (
      <VStack justify="flex-start" align="start" gap={2} flex={2} w="full">
        <Button variant="link" colorScheme="blue" onClick={() => navigate(backTo)} gap="2">
          <Icon.ArrowLeft /> Back to Investors Tab
        </Button>
        <Paper p="5" w="full">
          <Heading fontWeight="bold">{vaultId ? "Edit Individual" : "Add Individual"}</Heading>
          <BannerAlert mt={4} status="error" title="Investor not found" description="We couldn't load this investor." />
        </Paper>
      </VStack>
    );
  }

  return <EditIndividualFormBody vault={vault} backTo={backTo} />;
}

type EditIndividualFormBodyProps = {
  vault?: DTO.Vault;
  backTo: string;
};

function EditIndividualFormBody({ vault, backTo }: EditIndividualFormBodyProps): React.JSX.Element {
  const { investment } = useInvestmentContext();
  const navigate = useNavigate();
  const toast = useToast();
  const confirmModal = useDisclosure();
  const [signerData, setSignerData] = useState<Partial<IndividualSchema>>();

  const isPrimary = !!vault && vault.id === investment?.vault?.id;
  const isEntityNonPrimary = !isPrimary && investment?.vault?.entity_type === ENTITY;

  const updateVault = useUpdateVault(vault?.id as string, investment?.id as string);
  const addSigner = useCreateSigner(investment?.id as string);
  const createNote = useInvestmentNoteCreate(investment?.id);

  const isSaving = updateVault.isLoading || addSigner.isLoading || createNote.isLoading;

  const onConfirm = async (reason: string) => {
    try {
      if (!signerData) return null;
      if (vault?.id) {
        await updateVault.mutateAsync(signerData);
        toast({
          status: "success",
          description: "Successfully updated investor's data.",
        });
      } else {
        await addSigner.mutateAsync(signerData);
        toast({
          status: "success",
          description: "Successfully added investor to the investment.",
        });
      }
      await createNote.mutateAsync({
        content: `**Investors Updated**: ${reason}`,
      });
      navigate(backTo, { state: { refetch: true } });
    } catch (e) {
      console.error(e);
      toast({
        status: "error",
        description: extractErrorMessage(e),
      });
    } finally {
      confirmModal.onClose();
    }
  };

  const onSubmit = (data: IndividualSchema) => {
    confirmModal.onOpen();
    setSignerData(data);
  };

  const methods = useForm<DTO.Vault & { email?: string }>({
    defaultValues: {
      first_name: vault?.first_name,
      middle_name: vault?.middle_name,
      last_name: vault?.last_name,
      phone_number: vault?.phone_number,
      citizenship: vault?.citizenship,
      tax_id: vault?.tax_id,
      birthdate:
        vault?.birthdate && dayjs(vault?.birthdate).isValid()
          ? dayjs(vault?.birthdate).format("MM/DD/YYYY")
          : undefined,
      address_street_1: vault?.address_street_1,
      address_street_2: vault?.address_street_2,
      address_city: vault?.address_city,
      address_state: vault?.address_state,
      address_postal_code: vault?.address_postal_code,
      address_country_code: vault?.address_country_code,
      is_kyc: vault?.is_kyc,
      is_signer: vault?.is_signer,
    },
    resolver: zodResolver(buildSchema(isPrimary, !vault, isEntityNonPrimary)) as Resolver<
      DTO.Vault & { email?: string }
    >,
  });

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  const hidePostalCode = usePostalCodeExcluded(
    methods.watch("address_country_code"),
    methods.watch("address_postal_code"),
    () => methods.setValue("address_postal_code", ""),
  );

  return (
    <SmartFormProvider form={methods}>
      {({ Input, PhoneField, Select, DateInput, RadioGroup }) => (
        <VStack justify="flex-start" align="start" gap={2} flex={2} w="full">
          <Button variant="link" colorScheme="blue" onClick={() => navigate(backTo)} gap="2">
            <Icon.ArrowLeft /> Back to Investors Tab
          </Button>
          <Paper p="5" w="full">
            <Heading fontWeight="bold">{vault ? "Edit Individual" : "Add Individual"}</Heading>
            <Grid templateColumns="repeat(2, 1fr)" gap={4} mt={4}>
              <GridItem gridColumn={1}>
                <Input name="first_name" label="First Name" isRequired />
              </GridItem>

              <GridItem gridColumn={1}>
                <Input name="middle_name" label="Middle Name" />
              </GridItem>
              <GridItem gridColumn={1}>
                <Input name="last_name" label="Last Name" isRequired />
              </GridItem>

              <GridItem gridColumn={2} gridRow={1}>
                <PhoneField name="phone_number" label="Mobile Phone Number" isRequired />
              </GridItem>
              {!vault && (
                <GridItem gridColumn={2} gridRow={3}>
                  <Input name="email" label="Email" isRequired />
                </GridItem>
              )}
              {vault && (
                <GridItem gridColumn={2} gridRow={2}>
                  <Select
                    name="citizenship"
                    label="Citizenship"
                    isRequired={isPrimary}
                    options={[
                      { label: "US Citizen", value: US_CITIZEN },
                      { label: "International", value: INTERNATIONAL },
                    ]}
                    placeholder="Any"
                    controlProps={{ mb: 2 }}
                  />
                </GridItem>
              )}

              {isEntityNonPrimary && (
                <>
                  <GridItem gridColumn={1}>
                    <Text mb="2">Is this person a beneficial owner?</Text>
                    <RadioGroup name="is_kyc" direction="horizontal" options={yesNoOptions} />
                  </GridItem>
                  <GridItem gridColumn={2} gridRow={4}>
                    <Text mb="2">Is this individual required to sign agreements?</Text>
                    <RadioGroup name="is_signer" direction="horizontal" options={yesNoOptions} />
                  </GridItem>
                  {errors.is_signer?.message && (
                    <GridItem colSpan={2}>
                      <BannerAlert description={errors.is_signer.message} status="error" />
                    </GridItem>
                  )}
                </>
              )}
            </Grid>

            {vault && (
              <>
                <Heading fontWeight="bold" mt="4">
                  Personal Information
                </Heading>
                <Grid templateColumns="repeat(2, 1fr)" gap={4} mt={4}>
                  <GridItem colSpan={1}>
                    <Input name="tax_id" label="SSN/TIN" isRequired={isPrimary} />
                  </GridItem>
                  <GridItem colSpan={1}>
                    <DateInput
                      name="birthdate"
                      label="Birthdate (MM/DD/YYYY)"
                      mt={0}
                      placeholder="MM/DD/YYYY"
                      isRequired={isPrimary}
                    />
                  </GridItem>
                </Grid>

                <Heading fontWeight="bold" mt="4">
                  Address
                </Heading>
                <Text color="gray.500" mb={4}>
                  Enter the address of this individual.
                </Text>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem colSpan={1}>
                    <Input name="address_street_1" label="Street 1" isRequired={isPrimary} />
                  </GridItem>
                  <GridItem colSpan={1}>
                    <Input name="address_street_2" label="Street 2" />
                  </GridItem>
                  <GridItem colSpan={1}>
                    <Input name="address_city" label="City" isRequired={isPrimary} />
                  </GridItem>
                  <GridItem colSpan={1}>
                    <Input name="address_state" label="State" isRequired={isPrimary} />
                  </GridItem>
                  {!hidePostalCode && (
                    <GridItem colSpan={1}>
                      <Input name="address_postal_code" label="Postal Code" isRequired={isPrimary} />
                    </GridItem>
                  )}
                  <GridItem colSpan={1}>
                    <Select
                      name="address_country_code"
                      label="Country"
                      isRequired={isPrimary}
                      options={COUNTRY_OPTIONS}
                    />
                  </GridItem>
                </Grid>
              </>
            )}

            <ButtonGroup mt="4" spacing="4">
              <Button isLoading={isSaving} onClick={handleSubmit(onSubmit)} colorScheme="purple">
                Save Changes
              </Button>
              <Button onClick={() => navigate(backTo)} variant="outline">
                Discard Changes
              </Button>
            </ButtonGroup>
          </Paper>

          <ConfirmChangesModal
            isOpen={confirmModal.isOpen}
            isLoading={isSaving}
            isAccountTypeChanged={false}
            onClose={confirmModal.onClose}
            onConfirm={async (reason) => {
              await onConfirm(reason);
            }}
          />
        </VStack>
      )}
    </SmartFormProvider>
  );
}
